import React from "react";
import clsx from "clsx";

export interface ToggleGroupOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface ToggleGroupProps {
  options: ToggleGroupOption[];
  value: string;
  onChange: (value: string) => void;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "outline" | "ghost";
  orientation?: "horizontal" | "vertical";
  className?: string;
  disabled?: boolean;
}

/**
 * ToggleGroup - A group of toggle buttons for selecting one option
 * Perfect for time period selectors, filter options, etc.
 */
export const ToggleGroup: React.FC<ToggleGroupProps> = ({
  options,
  value,
  onChange,
  size = "md",
  variant = "default",
  orientation = "horizontal",
  className,
  disabled = false,
}) => {
  const sizeStyles = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-base",
    lg: "px-6 py-3 text-lg",
  };

  const variantStyles = {
    default: {
      container: "bg-slate-800/50 rounded-lg p-1",
      active: "bg-blue-600 text-white shadow-lg",
      inactive: "text-slate-400 hover:text-white hover:bg-slate-700",
    },
    outline: {
      container: "border border-slate-600 rounded-lg p-1",
      active: "bg-slate-700 text-white border-slate-500",
      inactive: "text-slate-400 hover:text-white hover:bg-slate-800",
    },
    ghost: {
      container: "rounded-lg p-1",
      active: "bg-slate-700/50 text-white",
      inactive: "text-slate-400 hover:text-white hover:bg-slate-800/50",
    },
  };

  const orientationStyles = {
    horizontal: "flex space-x-1",
    vertical: "flex flex-col space-y-1",
  };

  const currentVariant = variantStyles[variant];
  const currentSize = sizeStyles[size];
  const currentOrientation = orientationStyles[orientation];

  return (
    <div className={clsx(currentVariant.container, currentOrientation, className)}>
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => !disabled && !option.disabled && onChange(option.value)}
          disabled={disabled || option.disabled}
          className={clsx(
            "rounded-md transition-all duration-200 font-medium",
            currentSize,
            value === option.value ? currentVariant.active : currentVariant.inactive,
            (disabled || option.disabled) && "opacity-50 cursor-not-allowed",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

ToggleGroup.displayName = "ToggleGroup";
